import { useState, useEffect } from "react"
import styled from "styled-components"
import moment from "moment"
import { toast } from "react-toastify"

import PATHS, { getFullPath } from "~constants/paths"
import colors from "~constants/colors"

import { isPerkActive } from "./utils/perk"
import { getFullImagePath } from "~utils/utils"

import { RBAC_RULES } from "~rbac/constants/rbac-rules"
import IsAuthorized from "~rbac/isAuthorized"
import checkAuthorization from "~rbac/utils/checkAuthorization"

import BackLink from "~components/BackLink"
import PerkModal from "~components/perks/PerkModal"
import SkeletonWrapper from "~components/SkeletonWrapper"
import PerkRequestModal from "~components/perks/PerkRequestModal"
import EditableField from "~components/perks/EditableField"
import {
  ButtonPrimarySoftLarge,
  ButtonSecondarySoftLarge,
} from "~styled/button"

import PerkService from "~services/PerkService"
import { PerkShape, PostPerkShape } from "~shapes/perk"
import { OnRequestCloseProps } from "~shapes/modal"
import AppSimulatorModal from "~components/app-simulator/AppSimulatorModal"
import AppSimulator from "~components/app-simulator"
import PerkDetailScreen from "~components/app-simulator/PerkDetailScreen"

export interface PerkDetailProps {
  perk: PerkShape
  isLoading: boolean
}

const formatDate = (d?: string) =>
  d ? moment(d).format("MMM D, YYYY · h:mm A") : "--"

const PerkDetail = ({ perk, isLoading }: PerkDetailProps) => {
  const [perkData, setPerkData] = useState<PerkShape>(perk)
  const [showPerkPreview, setShowPerkPreview] = useState(false)
  const [isPerkModalOpened, setIsPerkModalOpened] = useState(false)

  useEffect(() => {
    setPerkData(perk)
  }, [perk])

  const handleOnRequestClose = ({ data }: OnRequestCloseProps) => {
    const p: PerkShape = data
    if (p) setPerkData({ ...p, company: perkData.company })
    setIsPerkModalOpened(false)
  }

  // Whether the current user may inline-edit a single field.
  const canEdit = checkAuthorization(RBAC_RULES.UPDATE_PERK)

  // Patches a single perk field and merges the response back into local state.
  // Rejects on failure so EditableField keeps the editor open with the input.
  const handleFieldSave =
    (field: keyof PostPerkShape) => async (value: string) => {
      try {
        const updated: PerkShape = await PerkService.patchPerk({
          id: perkData.id,
          [field]: value,
        })
        // Preserve the populated company projection the patch response omits.
        setPerkData(prev => ({ ...prev, ...updated, company: prev.company }))
        toast.success("Perk updated")
      } catch (err) {
        toast.error("Failed to update the Perk")
        throw err
      }
    }

  const isActive = perk && isPerkActive(perk)

  return (
    <>
      <BackLink text="Back to Perks" path={`${getFullPath(PATHS.perks)}`} />

      <PageCard>
        {/* ── Cover ── */}
        {perkData?.cover && (
          <CoverWrapper>
            <CoverImage src={perkData.cover} alt={perkData.name} />
          </CoverWrapper>
        )}

        {/* ── Header ── */}
        <Header>
          <HeaderLeft>
            <SkeletonWrapper isLoading={isLoading} width={11} height={11}>
              <StatusDot $active={!!isActive} />
            </SkeletonWrapper>

            <IsAuthorized action={RBAC_RULES.SEE_PERK_COMPANY}>
              <SkeletonWrapper isLoading={isLoading} width={44} height={44}>
                <CompanyLogo
                  src={getFullImagePath(perk?.company?.logo)}
                  alt={perk?.company?.name}
                />
              </SkeletonWrapper>
            </IsAuthorized>

            <TitleGroup>
              <SkeletonWrapper isLoading={isLoading} width={160}>
                <PerkTitle>
                  {checkAuthorization(RBAC_RULES.SEE_PERK_COMPANY)
                    ? perk?.company?.name
                    : perk?.name}
                </PerkTitle>
              </SkeletonWrapper>
              {perkData?.code && <PerkCode>{perkData.code}</PerkCode>}
            </TitleGroup>

            <StatusBadge $active={!!isActive}>
              {isActive ? "Active" : "Inactive"}
            </StatusBadge>
          </HeaderLeft>

          <HeaderActions>
            <ButtonSecondarySoftLarge
              disabled={isLoading}
              onClick={() => setShowPerkPreview(true)}
            >
              Preview
            </ButtonSecondarySoftLarge>
            <ButtonPrimarySoftLarge
              disabled={isLoading}
              onClick={() => setIsPerkModalOpened(true)}
            >
              Edit Perk
            </ButtonPrimarySoftLarge>
          </HeaderActions>
        </Header>

        <Divider />

        {/* ── Quick info grid ── */}
        <InfoGrid>
          {checkAuthorization(RBAC_RULES.SEE_PERK_COMPANY) &&
            perkData?.name && (
              <InfoCell $span={2}>
                <SkeletonWrapper isLoading={isLoading}>
                  <EditableField
                    label="Perk Name"
                    value={perkData?.name}
                    canEdit={canEdit}
                    onSave={handleFieldSave("name")}
                  />
                </SkeletonWrapper>
              </InfoCell>
            )}

          <InfoCell>
            <SkeletonWrapper isLoading={isLoading}>
              <EditableField
                label="Short Description"
                value={perkData?.shortDescription}
                canEdit={canEdit}
                onSave={handleFieldSave("shortDescription")}
              />
            </SkeletonWrapper>
          </InfoCell>

          <InfoCell>
            <Label>Status</Label>
            <SkeletonWrapper isLoading={isLoading}>
              <Value>{perkData?.status || "--"}</Value>
            </SkeletonWrapper>
          </InfoCell>

          <InfoCell>
            <Label>Logo</Label>
            <SkeletonWrapper isLoading={isLoading} width={56} height={56}>
              {perkData?.logo ? (
                <PerkLogo
                  src={getFullImagePath(perkData.logo)}
                  alt={`${perkData?.name} logo`}
                />
              ) : (
                <Value>--</Value>
              )}
            </SkeletonWrapper>
          </InfoCell>

          <InfoCell>
            <SkeletonWrapper isLoading={isLoading}>
              <EditableField
                label="Rules (Human Readable)"
                value={perkData?.rules}
                type="textarea"
                canEdit={canEdit}
                onSave={handleFieldSave("rules")}
              />
            </SkeletonWrapper>
          </InfoCell>

          <InfoCell>
            <Label>Eligibility Rules</Label>
            <SkeletonWrapper isLoading={isLoading}>
              <CodeValue>{perkData?.eligibilityRules || "--"}</CodeValue>
            </SkeletonWrapper>
          </InfoCell>

          <InfoCell>
            <SkeletonWrapper isLoading={isLoading}>
              <EditableField
                label="Redeem Resource"
                value={perkData?.redeemResource}
                canEdit={canEdit}
                onSave={handleFieldSave("redeemResource")}
              />
            </SkeletonWrapper>
          </InfoCell>

          <InfoCell>
            <SkeletonWrapper isLoading={isLoading}>
              <EditableField
                label="Success Redemption Title"
                value={perkData?.successRedemptionTitle}
                canEdit={canEdit}
                onSave={handleFieldSave("successRedemptionTitle")}
              />
            </SkeletonWrapper>
          </InfoCell>

          <InfoCell>
            <Label>Dates</Label>
            <SkeletonWrapper isLoading={isLoading}>
              <>
                <DateRow>
                  <DateLabel>Created</DateLabel>
                  <DateValue>{formatDate(perkData?.createdAt)}</DateValue>
                </DateRow>
                <DateRow>
                  <DateLabel>Updated</DateLabel>
                  <DateValue>{formatDate(perkData?.updatedAt)}</DateValue>
                </DateRow>
              </>
            </SkeletonWrapper>
          </InfoCell>
        </InfoGrid>

        <Divider />

        {/* ── Long content sections ── */}
        <LongSection>
          <SkeletonWrapper isLoading={isLoading} count={4}>
            <EditableField
              label="Description"
              value={perkData?.description}
              type="markdown"
              canEdit={canEdit}
              onSave={handleFieldSave("description")}
            />
          </SkeletonWrapper>
        </LongSection>

        <LongSection>
          <SkeletonWrapper isLoading={isLoading} count={4}>
            <EditableField
              label="Redeem Instructions"
              value={perkData?.redeemInstructions}
              type="markdown"
              canEdit={canEdit}
              onSave={handleFieldSave("redeemInstructions")}
            />
          </SkeletonWrapper>
        </LongSection>

        <LongSection>
          <EditableField
            label="Terms & Conditions"
            value={perkData?.termsAndConditions}
            type="markdown"
            canEdit={canEdit}
            onSave={handleFieldSave("termsAndConditions")}
          />
        </LongSection>
      </PageCard>

      {isPerkModalOpened && (
        <>
          <IsAuthorized action={RBAC_RULES.CREATE_PERK}>
            <PerkModal perk={perkData} onRequestClose={handleOnRequestClose} />
          </IsAuthorized>
          <IsAuthorized action={RBAC_RULES.CREATE_PERK_REQUEST}>
            <PerkRequestModal
              perk={perkData}
              onRequestClose={handleOnRequestClose}
            />
          </IsAuthorized>
        </>
      )}

      {showPerkPreview && (
        <AppSimulatorModal
          isOpen={showPerkPreview}
          onRequestClose={() => setShowPerkPreview(false)}
        >
          <AppSimulator>
            <PerkDetailScreen perk={perk} />
          </AppSimulator>
        </AppSimulatorModal>
      )}
    </>
  )
}

/* ── Styled Components ── */

const PageCard = styled.div`
  background: ${colors.white};
  border: 1px solid ${colors.gray9};
  border-radius: 12px;
  margin-top: 16px;
  overflow: hidden;
`

const CoverWrapper = styled.div`
  width: 100%;
  max-height: 220px;
  overflow: hidden;
  background: ${colors.gray1};
`

const CoverImage = styled.img`
  width: 100%;
  height: 220px;
  object-fit: cover;
  display: block;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  padding: 24px 32px 0;
`

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const StatusDot = styled.span<{ $active: boolean }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ $active }) => ($active ? colors.caribbean : colors.gray4)};
`

const CompanyLogo = styled.img`
  width: 44px;
  height: 44px;
  border-radius: 8px;
  object-fit: contain;
  border: 1px solid ${colors.gray9};
`

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
`

const PerkTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: ${colors.gray3};
  line-height: 1.3;
`

const PerkCode = styled.span`
  font-size: 12px;
  color: ${colors.gray6};
  font-weight: 500;
  letter-spacing: 0.3px;
`

const StatusBadge = styled.span<{ $active: boolean }>`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 3px 10px;
  border-radius: 12px;
  color: ${({ $active }) => ($active ? "#166534" : colors.gray6)};
  background: ${({ $active }) => ($active ? "#f0fdf4" : colors.gray1)};
`

const HeaderActions = styled.div`
  display: flex;
  gap: 10px;
`

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${colors.gray9};
  margin: 20px 32px;
`

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px 32px;
  padding: 0 32px;
`

const InfoCell = styled.div<{ $span?: number }>`
  grid-column: ${({ $span }) => ($span ? `span ${$span}` : "auto")};
`

const Label = styled.div`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${colors.gray6};
  margin-bottom: 6px;
`

const Value = styled.div`
  font-size: 14px;
  color: ${colors.gray3};
  line-height: 1.5;
`

const CodeValue = styled.code`
  display: block;
  font-size: 13px;
  color: ${colors.blue1};
  background: ${colors.gray5};
  padding: 6px 10px;
  border-radius: 4px;
  font-family: "SF Mono", "Fira Code", "Fira Mono", Menlo, monospace;
  word-break: break-all;
`

const DateRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  &:not(:last-child) {
    margin-bottom: 4px;
  }
`

const DateLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${colors.gray6};
  min-width: 60px;
`

const DateValue = styled.span`
  font-size: 13px;
  color: ${colors.gray3};
`

const LongSection = styled.div`
  padding: 0 32px;
  margin-bottom: 24px;
`

const PerkLogo = styled.img`
  width: 56px;
  height: 56px;
  border-radius: 8px;
  object-fit: contain;
  border: 1px solid ${colors.gray9};
  background: ${colors.white};
`

export default PerkDetail
